import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Pagination } from '@mui/material';
import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  Button,
  FileDropZone,
  SvgExport,
  Table,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { bytesToMB } from '../../../core-utils/helper-utils/contractAIQHelper';
import InfoBar from '../../atoms/InfoBar';
import properties from '../../../core-utils/properties';
import axios from 'axios';
import {
  convertToJSONString,
  promptChain,
} from '../../../core-utils/helper-utils/contractMetadataHelper';
import Loader from '../../molecules/Loader';
import GifWithLabels from '@HomaHealth/ui-component-library/lib/_patient-access-components/molecules/GIFWithLabels';
import NoResults from '../../../../public/Images/NoResults.png';
import contractAIQConstants from '../../../core-utils/messages/ContractAIQConstants';
import clsx from 'clsx';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { Close } from '@HomaHealth/ui-component-library';
import { getDocumentSelectionTableColumns } from '../../../core-utils/helper-utils/docComparisonHelper';
import useSnackbar from '../../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../atoms/Snackbar';
import { MultiFilterComponent } from '../../molecules/MultiFilter';
import { useStores } from '../../../stores';

export interface DocumentsSelectionProps {
  vendorType?: string;
  setSelectedContracts: React.Dispatch<React.SetStateAction<any[]>>;
  eg_nid?: string;
  hideUploadOption?: boolean;
  hideFilter?: boolean;
  showPlanDocs?: boolean;
  comparisonName?: string;
}

interface UploadedDocumentsType {
  document_name: string;
  document_id: string;
  service_agreement_id: string;
  health_care_partner_id: string;
  document_type: string;
  document_effective_date: string;
  vendor?: string;
  serviceAgreement?: string;
}

const useStyles = makeStyles()({
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  chooseDocHeader: {
    display: 'flex',
    gap: '200px',
  },

  tableRoot: {
    padding: '0px',
  },
  tableHeaderText: {
    fontWeight: 500,
    color: patientAccessTheme.palette.text.highemphasis,
    fontSize: '12px',
    lineHeight: '12px',
  },
  tableFirstColumn: {
    width: 'max-content',
    paddingLeft: '0px',
  },
  tableCellText: {
    fontWeight: 400,
    color: patientAccessTheme.palette.text.mediumemphasis,
    fontSize: '14px',
    lineHeight: '24px',
  },
  tableTHForRowTop: {
    height: '48px',
    backgroundColor: patientAccessTheme.palette.grey[100],
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    textAlign: 'left',

    display: 'flex',
    alignItems: 'center',
  },
  tableTHSelection: {
    paddingLeft: '26px',
    // width: '106px',
  },
  tableRow: {
    height: 'auto',
    minHeight: '56px',
    padding: '0px',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    '& td:first-child': {
      paddingLeft: '26px',
    },
    '&:hover': {
      backgroundColor: patientAccessTheme.palette.grey[300],
    },
    display: 'flex',
    alignItems: 'center',
  },

  subRows: {
    backgroundColor: patientAccessTheme.palette.grey[100],
  },
  uploadFilesGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '48px',
  },
  uploadedFiles: {
    height: '233px',
    width: '60%',
    background: patientAccessTheme.palette.background.default,
    borderRadius: '4px',
    padding: '18px 12px',
  },
  files: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    height: '136px',
    overflow: 'auto',
    paddingRight: '8px',
    ...scrollBarStyles,
  },
  uploadModal: {
    width: '800px',
    maxHeight: '720px',
    backgroundColor: patientAccessTheme.palette.status.white,
    borderRadius: '8px',
    padding: '24px 32px 20px 32px',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    flexDirection: 'column',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'right',
    marginTop: '16px',
  },

  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: '1rem 0rem',
    alignItems: 'center',
  },
  noGroups: {
    display: 'flex',
    alignItems: 'center',
    height: '290px',
    justifyContent: 'center',
  },
});

const useTableStyles = makeStyles()({
  tableHeaderText: {
    fontWeight: 500,
    color: patientAccessTheme.palette.text.highemphasis,
    fontSize: '12px',
    lineHeight: '12px',
  },
  tableFirstColumn: {
    width: 'max-content',
  },
  tableCellText: {
    fontWeight: 400,
    color: patientAccessTheme.palette.text.mediumemphasis,
    fontSize: '14px',
    lineHeight: '24px',
  },
  subRows: {
    backgroundColor: patientAccessTheme.palette.grey[100],
    '&:first-child': {
      paddingLeft: '54px',
    },
  },
});

export const DocumentsSelection: React.FC<DocumentsSelectionProps> = ({
  vendorType,
  setSelectedContracts,
  eg_nid,
  hideUploadOption,
  showPlanDocs,
  hideFilter,
  comparisonName,
}) => {
  const { classes } = useStyles();
  const messages = contractAIQConstants.COMPLIANCE_ASSURANCE;
  const tableClasses = useTableStyles();
  const commonTableProps = {
    showPagination: false,
    showFiltering: false,
    showSelection: false,
    overallGridStyles: classes.tableRoot,
    classNameForTableHeaderRowTop: classes.tableTHForRowTop,
    classNameForTableHeader: classes.tableTHForRowTop,
    classNameForTableHeaderRowForSelection: clsx(
      classes.tableTHForRowTop,
      classes.tableTHSelection,
    ),
    classNameForTableDataRow: classes.tableRow,
    classNameForTableDataRowInDepth: clsx(classes.tableRow, classes.subRows),
    isManualPagination: true,
    controlledPageIndex: 0,
    showEditData: true,
    hideSelectionForSubRows: true,
    hideSelectionHeader: false,
  };
  const { openSnackbar, snackbarState, handleCloseSnackBar } = useSnackbar();
  const loginPageStore = useStores().loginPageStore;

  const [contractsData, setContractsData] = useState<any[]>([]);
  const [planDocuments, setPlanDocuments] = useState<any[]>([]);
  const [contractsLoading, setContractsLoading] = useState<boolean>(false);
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any>([]);
  const [serviceAgreements, setServiceAgreements] = useState<any>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocumentsType[]
  >([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [allGroupOptions, setAllGroupOptions] = useState<string[]>([]);
  const [allVendorOptions, setAllVendorOptions] = useState<string[]>([]);
  const [totalContracts, setTotalContracts] = useState<number>(0);

  const formattedContractsData = useMemo(() => {
    const data = showPlanDocs
      ? [
          ...contractsData.map((row: any) => ({
            ...row,
            id: row.contract_id,
            name: row.health_care_partner_name,
            isPlanDoc: false,
            subRows:
               
              row?.documents?.map((doc: any, innerIndex: any) => ({
                id: doc?._id || `${row?.contract_id}_doc_${innerIndex}`,
                document_name: doc?.document_name,
              })),
          })),
          ...planDocuments,
        ]
      : contractsData.map((row: any) => ({
          ...row,
          name:
            row?.record_type === 'draft'
              ? row?.draft_name
              : row?.health_care_partner_name,
          id:
            row?.record_type === 'draft'
              ? row?.draft_id?.toString()
              : row?.contract_id?.toString(),
          contract_type:
            row?.record_type === 'draft'
              ? 'Draft'
              : row?.status === 'Archive'
              ? 'Archive'
              : 'Active',
          subRows:
            row?.record_type === 'contract'
              ? row?.documents?.length
                ?  
                  row?.documents?.map((doc: any) => ({
                    id: doc?.document_id || `${row?.contract_id}_doc_0`,
                    document_name: doc?.document_name || '-',
                    document_type: doc?.document_type || '-',
                  }))
                : []
              : [
                  {
                    id:
                      row?.document?.document_id || `${row?.contract_id}_doc_0`,
                    document_name: row?.document?.document_name || '-',
                    document_type: row?.document?.document_type || '-',
                  },
                ],
        }));
    if (showPlanDocs) {
      setTotalPages(Math.ceil(data.length / 10));
      const start = (pageNumber - 1) * 10;
      const end = start + 10;
      return data.slice(start, end);
    }
    return data;
  }, [contractsData, planDocuments, pageNumber]);

  const handleSelectionChange = useCallback(
    (rowIds: string[] | string, selectedContracts: any) => {
      if (Array.isArray(rowIds) && Array.isArray(selectedContracts)) {
        setSelectedRowIds(rowIds);
        setSelectedContracts(selectedContracts);
        return;
      }
      setSelectedRowIds((prevRowIds) => {
        const isSelected = prevRowIds.includes(rowIds);
        const newRowIds = isSelected
          ?  
            prevRowIds.filter((id) => id !== rowIds)
          : [...prevRowIds, rowIds];

         
        setSelectedContracts((prevContracts) => {
          if (!selectedContracts) {
            return prevContracts;
          }
          return isSelected
            ? prevContracts.filter(
                 
                (contract) => {
                  if (selectedContracts.record_type === 'draft') {
                    return contract.draft_id !== selectedContracts.draft_id;
                  }
                  return contract.contract_id !== selectedContracts.contract_id;
                },
              )
            : [
                ...prevContracts,
                {
                  ...selectedContracts,
                  record_type: selectedContracts?.record_type ?? 'contract',
                  ...(selectedContracts?.record_type === 'draft'
                    ? { draft_id: selectedContracts?.draft_id }
                    : {}),
                },
              ];
        });

        return newRowIds;
      });
    },
    [],
  );

  const handleTablePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (value !== pageNumber) {
      setPageNumber(value);
    }
  };

  //Axios
  const uploadDocuments = async () => {
    const formData = new FormData();
    uploadedFiles.forEach((file: any) => {
      formData.append(`files`, file);
    });
    formData.append('employer', `dc-${comparisonName}`);
    await getServiceAgreements();

    try {
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.uploadContracts,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (res.status === 200) {
        const data = res?.data?.data;
        const updatedDocuments = data?.map((item: any) => ({
          document_id: item?._id,
          document_name: item?.document_name,
          document_url: item?.document_url,
          service_agreement_id: '',
          health_care_partner_id: '',
          document_type: '',
          document_effective_date: '',
        }));

        const documentUpdates = await Promise.all(
          updatedDocuments.map(async (doc: any) => {
            if (doc.document_url) {
              const updates = await extractDataFromContracts(
                doc.document_url,
                doc.document_id,
              );
              return updates ? { ...doc, ...updates } : doc;
            }
            return doc;
          }),
        );
        setUploadedDocuments(documentUpdates);
        await createContracts(documentUpdates);
      }
      return [];
    } catch (error: any) {
      openSnackbar(
        error?.response?.data?.message || 'Error uploading files',
        'error',
        2000,
      );
      return [];
    }
  };

  const getServiceAgreements = async () => {
    await axios
      .get(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getServiceAgreements,
      )
      .then((res: any) => {
        if (res.status === 200) {
          const data = res?.data?.data;
          setServiceAgreements(data);
        }
      })
      .catch((error) => {
        openSnackbar(error.response?.data?.error, 'error', 6000);
      });
  };

  const addHealthCarePartner = async (fields: any, isAIGenerated?: boolean) => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.addHealthcarePartner,
        {
          eg_nid: `dc_${comparisonName}`,
          healthCarePartnersFields: fields,
          is_AI_generated: isAIGenerated,
        },
      );

      if (response.status === 200) {
        return response.data.data; // Return the response data
      } else {
        throw new Error('Unexpected response status'); // Handle unexpected status codes
      }
    } catch (error: any) {
      openSnackbar(
        error?.response?.data?.message || 'Error adding healthcare partner',
        'error',
        2000,
      );
      return null; // Return null or an appropriate value in case of an error
    }
  };

  const getHealthCarePartners = async () => {
    return await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getHealthcarePartner,
        {
          eg_nid: `dc_${comparisonName}`,
        },
      )
      .then((res: any) => {
        if (res.status === 200) {
          const data = res?.data?.data;
          return data || [];
        } else {
          return [];
        }
      })
      .catch((error) => {
        openSnackbar(
          error?.response?.data?.message ||
            'Error fetching healthcare partners',
          'error',
          2000,
        );
        return [];
      });
  };

  const extractDataFromContracts = async (
    fileUrl: string,
    documentId: string,
  ) => {
    const prompt = `You are a classification model trained to analyze healthcare and retirement benefit vendor agreements and categorize vendors based on a provided reference CSV. You will also extract key metadata from the agreement for reporting purposes.\n\nYou will be given:\n1. A CSV file containing a list of standardized vendor types and their corresponding responsibilities.\n2. Contract/ Agreement\n\nYour task is to:\n1. Classify the vendor into one or more vendor types based on the CSV and the content of the agreement.\n2. Extract important metadata about the vendor and the agreement.\n\n---\n\nYour response must be returned as two separate JSON objects:\n\n1. vendor_classification: Contains vendor type and reasoning  \n2. document_metadata: Contains vendor name, agreement type, and effective date\n\nOutput Format:\n\n{sample_output}\n\nGuidelines:\n\nUse the vendor type CSV to classify the vendor based on the scope of responsibilities described in the agreement.\n\nIdeally, return only one vendor_type that most accurately matches the vendor’s role.\n\nOnly include multiple vendor types if the vendor clearly performs multiple distinct responsibilities that would not typically be delivered by a single vendor type in the CSV.\n\nIf no clear match exists, use "vendor_type": "Others" and explain in match_reason.\n\nExtract vendor_name from the opening paragraph or signature block.\n\nDetermine document_type:\n\nUse "base" for original agreements or if unspecified.\n\nUse "amendment" if modifying a previous agreement.\n\nExtract effective_date using terms like “Effective as of,” “Dated,” etc., in YYYY-MM-DD format or return null.\n\nVendor Types: {vendor_types}\nContract/ Agreement: {document}\n`;
    const outputFormat = convertToJSONString({
      vendor_classification: [
        {
          vendor_type: "<exact match from CSV or 'Others'>",
          match_reason: '<brief explanation of why this category was selected>',
        },
      ],
      document_metadata: {
        vendor_name: '<name of vendor extracted from the agreement>',
        document_type: '<base or amendment>',
        effective_date: '<YYYY-MM-DD format or null if not found>',
      },
    });

    try {
      const result = await promptChain(
        prompt,
        { sample_output: outputFormat },
        {
          vendor_types:
            'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Onboarding/Vendor Types.csv',
          document: fileUrl,
        },
        'gpt-5-mini',
        'https://storageblobprod.blob.core.windows.net/contract/static-uploads/Onboarding/Contracts_JSON_Schema.json',
      );

      const { document_metadata, vendor_classification } = result;
       
      const { match_reason } = vendor_classification?.[0];
      const { vendor_name, effective_date } = document_metadata;

      let svcAgreementId: string = '';
      let healthCarePartnerId: string = '';
      if (vendorType) {
        svcAgreementId = serviceAgreements?.find(
          (svc: any) => svc?.service_agreement_name === vendorType,
        )?._id;

        const identifier = serviceAgreements?.find(
          (item: any) => item?._id === svcAgreementId,
        )?.identifier;

        // Add health care partner if identifier exists
        if (identifier && vendor_name) {
          await addHealthCarePartner(
            {
              [identifier]: vendor_name,
            },
            true,
          );
        }

        // Fetch updated health care partners
        const updatedHealthcarePartners: any[] = await getHealthCarePartners();
        healthCarePartnerId =
          updatedHealthcarePartners?.find(
            (item: any) =>
              item?.service_agreement_id === svcAgreementId &&
              item?.health_care_partner === vendor_name,
          )?._id || '';
      }

      return {
        document_id: documentId,
        service_agreement_id: svcAgreementId,
        health_care_partner_id: healthCarePartnerId,
        document_type: 'Base Document',
        document_effective_date: effective_date,
        vendor: vendor_name,
        serviceAgreement: vendorType,
      };
    } catch (error: any) {
      openSnackbar(
        error?.response?.data?.message || 'Error extracting contracts',
        'error',
        2000,
      );
      return null;
    }
  };

  const createContracts = async (
    documentsToUpload: any[] = uploadedDocuments,
  ) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.addContract,
        {
          eg_nid: `dc_${comparisonName}`,
          documents: documentsToUpload,
        },
      )
      .then(async (res: any) => {
        if (res.status === 200) {
          getAllContractsAndDrafts();
          setIsUploadModalOpen(false);
          setPageNumber(1);
        }
      });
  };

  const getAllContractsAndDrafts = async (page = pageNumber) => {
    setContractsLoading(true);
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getAllContractsAndDrafts,
        {
          service_agreement_name: vendorType,
          health_care_partner_names: selectedVendors,
          employer_names: selectedGroups,
          eg_nids: loginPageStore?.isAdmin
            ? loginPageStore?.employerGroups
            : [],
          pageNo: page,
          limit: 10,
        },
      );
      const data = response?.data?.data;

      if (showPlanDocs) {
        setContractsData(data?.data || []);
      } else {
        setContractsData(data?.data || []);
        setTotalPages(data?.pagination?.total_pages || 0);
        setTotalContracts(data?.pagination?.total_records || 0);
      }
      setContractsLoading(false);
    } catch (error) {
      setContractsLoading(false);
    }
  };

  const getPlanDocuments = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getPlanDocs,
        {
          eg_nid: eg_nid,
        },
      )
      .then(async (res: any) => {
        if (res.status === 200) {
          const data = res?.data?.data || [];
          const formattedData = await Promise.all(
            data.map(async (item: any, index: number) => ({
              id: item?._id,
              name:
                item?.benefit_types && item.benefit_types.length > 0
                  ? item.benefit_types
                  : ['Medical'],
              total_files: item?.documents?.length ?? 0,
              employer_info: item?.employer_info,
              isPlanDoc: true,
              subRows:
                 
                item?.documents?.map((doc: any) => ({
                  id: doc?._id || `${item?._id}_doc_${index}`,
                  document_name: doc?.document_name,
                })) ?? [],
            })),
          );
          setPlanDocuments(formattedData);
        }
      })
      .catch((error) => {
        openSnackbar(error, 'error', 5000);
        throw error;
      });
  };

  const fetchAllGroupsAndVendors = async () => {
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getAllContractsAndDrafts,
        {
          service_agreement_name: vendorType,
          eg_nids: loginPageStore?.isAdmin
            ? loginPageStore?.employerGroups
            : [],
        },
      );

      if (response.status === 200) {
        const allContracts = response.data.data.data;

        // Extract unique group names and vendor names from the contracts
        const vendors = new Set(
          allContracts
            .map((item: any) => item.health_care_partner_name)
            .filter((name: any): name is string => typeof name === 'string'),
        );

        const employers = new Set(
          allContracts
            .map((item: any) => item.employer_name)
            .filter((name: any): name is string => typeof name === 'string'),
        );

        setAllGroupOptions(Array.from(employers) as string[]);
        setAllVendorOptions(Array.from(vendors) as string[]);
      }
    } catch (error) {
      // Handle error or show snackbar if you want
    }
  };

  // useEffects
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (showPlanDocs) {
      // only call once when pageNumber is 1
      if (pageNumber === 1) {
        getAllContractsAndDrafts(pageNumber);
        getPlanDocuments();
      }
    } else {
      getAllContractsAndDrafts(pageNumber);
    }
  }, [pageNumber, showPlanDocs]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false; // mark initial load done
      return;
    }
    setPageNumber(1);
    getAllContractsAndDrafts(1);
  }, [selectedGroups, selectedVendors, vendorType]);

  useEffect(() => {
    fetchAllGroupsAndVendors();
  }, [vendorType]);

  //Components
  const UploadFiles = (
    uploadedFiles: any[],
    setUploadedFiles: React.Dispatch<any>,
  ) => {
    return (
      <>
        <Grid className={classes.uploadFilesGrid}>
          {uploadedFiles?.length > 0 && (
            <Grid className={classes.uploadedFiles}>
              <Typography
                fontSize="12px"
                fontType="regular"
                lineHeight="24px"
                textColor={patientAccessTheme.palette.text.lowemphasis}
                margin="0px 0px 16px 0px"
              >
                {`${uploadedFiles?.length} Document${
                  uploadedFiles?.length > 1 ? 's' : ''
                } uploaded`}
              </Typography>
              <Grid className={classes.files}>
                {uploadedFiles?.map((item: any, index: number) => {
                  return (
                    <UploadedDocument
                      name={item?.name}
                      size={`${bytesToMB(item?.size)} MB`}
                      handleDelete={() => {
                        const updatedItems = [...uploadedFiles];
                        // Remove the item at the specified index
                        updatedItems.splice(index, 1);
                        // Update the state with the new array
                        setUploadedFiles(updatedItems);
                      }}
                      width="100%"
                      truncateWidth="250px"
                    />
                  );
                })}
              </Grid>
            </Grid>
          )}
          <FileDropZone
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            filesSupported={['.pdf']}
            width={uploadedFiles?.length > 0 ? '38%' : '100%'}
            height="233px"
            isMultiple={true}
          />
        </Grid>
        <InfoBar info={messages.UPLOAD_INFO} type="Info" width="100%" />
        <InfoBar
          info="Document is temporary, not linked to any employer group, and treated as the selected vendor type base document."
          type="Error"
          width="100%"
        />
      </>
    );
  };

  const UploadDocumentModal = () => {
    return (
      <Modal
        style={{ zIndex: 500 }}
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      >
        <Grid container className={classes.uploadModal}>
          <Grid
            container
            style={{ marginBottom: '20px' }}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                fontSize="18px"
                lineHeight="26px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin="0px 0px 4px 0px"
              >
                {messages.UPLOAD_MASTER_DOCUMENT + `(s) for ${vendorType}`}
              </Typography>
            </Grid>
            <Grid style={{ height: '14px', width: '14px' }}>
              <Close
                height={'14px'}
                width={'14px'}
                fill={patientAccessTheme.palette.status.icon}
                onClick={() => setIsUploadModalOpen(false)}
                cursor={'pointer'}
              />
            </Grid>
          </Grid>
          {UploadFiles(uploadedFiles, setUploadedFiles)}
          <Grid
            container
            style={{ marginTop: '12px' }}
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Button
              label={'Upload'}
              variant="contained"
              height="37px"
              width="auto"
              disabled={false}
              onClick={() => {
                uploadDocuments();
              }}
            />
          </Grid>
        </Grid>
      </Modal>
    );
  };

  return (
    <>
      <Grid className={classes.buttonContainer}>
        {!hideFilter && (
          <Grid>
            <MultiFilterComponent
              filterOptions={[
                {
                  label: 'Group Name',
                  options: allGroupOptions || [],
                  selected: selectedGroups,
                  onChange: (newSelectedGroups) => {
                    setSelectedVendors([]);
                    setSelectedGroups(newSelectedGroups);
                  },
                },
                {
                  label: 'Vendor Name',
                  options: allVendorOptions || [],
                  selected: selectedVendors,
                  onChange: (newSelectedVendors) => {
                    setSelectedGroups([]);
                    setSelectedVendors(newSelectedVendors);
                  },
                },
              ]}
            />
          </Grid>
        )}
        {!hideUploadOption && (
          <Button
            label={'Upload Contracts'}
            variant="contained"
            height="36px"
            width="auto"
            startIcon={
              <SvgExport
                fill={patientAccessTheme.palette.status.white}
                width={'20px'}
                height={'20px'}
              />
            }
            onClick={() => {
              setUploadedFiles([]);
              setIsUploadModalOpen(true);
            }}
          />
        )}
      </Grid>
      {contractsLoading ? (
        <Loader />
      ) : (
        <>
          {contractsData?.length > 0 ? (
            <>
              <Table
                {...commonTableProps}
                data={formattedContractsData}
                columns={getDocumentSelectionTableColumns(
                  tableClasses,
                  selectedRowIds,
                  handleSelectionChange,
                  showPlanDocs,
                  totalContracts,
                )}
                onHoverRowData={() => {}}
                showPagination={false}
                isManualPagination={true}
                // emptyDataWidget={}
              />
              {totalPages > 1 && (
                <div className={classes.pagination}>
                  <Pagination
                    page={pageNumber}
                    count={totalPages}
                    onChange={handleTablePageChange}
                    size="small"
                    color="primary"
                  />
                </div>
              )}
            </>
          ) : (
            <Grid className={classes.noGroups}>
              <GifWithLabels
                mainLabel={
                  // <Typography
                  //   fontSize="16px"
                  //   fontType="medium"
                  //   lineHeight="24px"
                  //   textColor={patientAccessTheme.palette.text.mediumemphasis}
                  // >
                  //   {'No Groups Exist !'}
                  // </Typography>
                  'No Documents for comparison'
                }
                subLabel={
                  'There are currently no contracts available for comparison '
                }
                gifUrl={NoResults}
              />
            </Grid>
          )}
        </>
      )}
      <UploadDocumentModal />
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};
